import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {CrmSalesforceListModel} from '../../../../../models/api/crmSalesforceListModel';
import {CrmSyncSearchSalesforceListsModel} from '../../../../../models/api/crmSearchSalesforceLists';
import {CrmSyncSearchOptions} from '../../../../../store/data/crmSync/crmSyncsActions';
import {DropdownItemMultiline} from '../../../../common/dropdowns/dropdownItemMultiline';
import {DeprecatedSearchDropdown} from '../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {useAsyncDropdownSource} from '../../../../common/dropdowns/source/hooks/useAsyncDropdownSource';

/*
 * Constants.
 */

const dropdownItemHeight = 33;
const dropdownHeight = 342;
const searchLimit = 100;

/*
 * Props.
 */

interface SalesforceListsDropdownProps {
  popoverProps: PopoverTransferProps;
  selectedListId?: string | number;
  onSelect: HandlerOf<CrmSalesforceListModel>;
  searchSalesforceAccountListsAsync: (
    query: string,
    options: CrmSyncSearchOptions,
  ) => Promise<CrmSyncSearchSalesforceListsModel | undefined>;
}

interface SalesforceListsDropdownPaginationData {
  offset?: number;
}

/*
 * Styles.
 */

interface StyledDropdownItemProps {
  $isSelected?: boolean;
}

const StyledDropdownItem = styled(DropdownItem)<StyledDropdownItemProps>`
  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

/*
 * Component.
 */

export const SalesforceListsDropdown: FC<SalesforceListsDropdownProps> = (props) => {
  const {popoverProps, selectedListId, onSelect, searchSalesforceAccountListsAsync} = props;
  const dropdownSource = useAsyncDropdownSource<
    CrmSalesforceListModel,
    SalesforceListsDropdownPaginationData
  >(
    {
      fetcher: async (query, items, cancelToken, paginationData) => {
        const response = await searchSalesforceAccountListsAsync(query, {
          limit: searchLimit,
          offset: paginationData?.offset,
          cancelToken,
        });
        cancelToken.throwIfRequested();

        return {
          items: [...items, ...(response?.lists ?? [])],
          canLoadMore: Boolean(response?.hasMore),
          paginationData: {
            offset: (paginationData?.offset ?? 0) + searchLimit,
          },
        };
      },
      renderRow: (item: CrmSalesforceListModel) => (
        <StyledDropdownItem
          onSelect={() => onSelect(item)}
          isChecked={selectedListId === item.id}
          $isSelected={selectedListId === item.id}
        >
          <DropdownItemMultiline title={item.name} />
        </StyledDropdownItem>
      ),
      rowHeightPredicate: () => dropdownItemHeight,
    },
    [],
  );

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={renderDropdownTitle()}
      source={dropdownSource}
      height={dropdownHeight}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownTitle() {
  return <Trans id="accounts-crm-salesforce-lists-dropdown-title">Salesforce list views</Trans>;
}
